const TURKISH_LOCALE = "tr-TR";
const ELEVATED_POST_ROLES = new Set([
    "admin",
    "company_admin",
    "manager",
    "team_manager",
]);
const ADMIN_ROLES = new Set(["super_admin", "admin"]);
const TEAM_MANAGER_ROLES = new Set(["manager", "team_manager"]);
const RELATIVE_TIME_FALLBACK_LABELS = {
    second: "sn",
    minute: "dk",
    hour: "sa",
    day: "gün",
    month: "ay",
    year: "yıl",
};

const relativeTimeFormatter =
    typeof Intl !== "undefined" && typeof Intl.RelativeTimeFormat === "function"
        ? new Intl.RelativeTimeFormat(TURKISH_LOCALE, {
            numeric: "always",
            style: "short",
        })
        : null;

const RELATIVE_TIME_UNITS = [
    { limit: 60, divisor: 1, unit: "second" },
    { limit: 60 * 60, divisor: 60, unit: "minute" },
    { limit: 24 * 60 * 60, divisor: 60 * 60, unit: "hour" },
    { limit: 30 * 24 * 60 * 60, divisor: 24 * 60 * 60, unit: "day" },
    {
        limit: 365 * 24 * 60 * 60,
        divisor: 30 * 24 * 60 * 60,
        unit: "month",
    },
    { limit: Infinity, divisor: 365 * 24 * 60 * 60, unit: "year" },
];

export function userName(user, fallback = "Kullanıcı") {
    const fullName = user?.fullname || user?.full_name;
    if (fullName) return fullName;

    return (
        [user?.name, user?.surname].filter(Boolean).join(" ").trim() || fallback
    );
}

export function userPhoto(user) {
    const photoCandidates = [
        user?.photo_link,
        user?.profile_photo_url,
        user?.profile_photo,
        user?.avatar,
        user?.photo,
        user?.image,
    ];

    for (const photo of photoCandidates) {
        if (typeof photo === "string" && photo.trim()) return photo;
        const nestedPhoto = photo?.url || photo?.file_link || photo?.src;
        if (typeof nestedPhoto === "string" && nestedPhoto.trim()) {
            return nestedPhoto;
        }
    }

    return "";
}

export function userInitials(userOrName, fallback = "K") {
    const name =
        typeof userOrName === "string"
            ? userOrName
            : userName(userOrName, fallback);
    const parts = name.split(/\s+/).filter(Boolean);
    if (!parts.length) return fallback;

    const first = parts[0].charAt(0);
    const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : "";
    return `${first}${last}`.toLocaleUpperCase(TURKISH_LOCALE);
}

export function postRoleLabel(post) {
    const user = post?.user || {};
    const explicitLabel = post?.role_label || user.role_label;
    if (explicitLabel) return explicitLabel;

    const hasElevatedRole = (Array.isArray(user.roles) ? user.roles : []).some(
        (role) =>
            ELEVATED_POST_ROLES.has(typeof role === "string" ? role : role?.name)
    );

    return hasElevatedRole ? "Yönetici" : "";
}

export function isAwaitingAdminApproval(item) {
    return Number(item?.status_id) === 1;
}

export function likedUserRoleLabel(user) {
    const roleNames = (Array.isArray(user?.roles) ? user.roles : []).map((role) =>
        typeof role === "string" ? role : role?.name
    );

    if (roleNames.some((role) => ADMIN_ROLES.has(role))) {
        return "Yönetici";
    }
    if (roleNames.includes("company_admin")) return "Şirket yöneticisi";
    if (roleNames.some((role) => TEAM_MANAGER_ROLES.has(role))) {
        return "Takım yöneticisi";
    }
    return "";
}

export function relativeTime(value, now = Date.now()) {
    const preformatted = value?.time_ago || value?.created_at_human;
    if (preformatted) return preformatted;

    const dateValue =
        value && typeof value === "object" ? value.created_at : value;
    if (!dateValue) return "";

    const timestamp = new Date(dateValue).getTime();
    if (Number.isNaN(timestamp)) return "";

    const differenceInSeconds = (timestamp - now) / 1000;
    const absoluteDifference = Math.abs(differenceInSeconds);
    if (absoluteDifference < 60) return "şimdi";

    const { divisor, unit } = RELATIVE_TIME_UNITS.find(
        ({ limit }) => absoluteDifference < limit
    );
    const roundedAmount = Math.round(absoluteDifference / divisor);
    const amount = differenceInSeconds < 0 ? -roundedAmount : roundedAmount;

    if (relativeTimeFormatter) return relativeTimeFormatter.format(amount, unit);

    const suffix = amount < 0 ? "önce" : "sonra";
    return `${Math.abs(amount)} ${RELATIVE_TIME_FALLBACK_LABELS[unit]} ${suffix}`;
}
